import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HiMenu, HiX, HiPhone } from "react-icons/hi";
import { cn } from "@/utils/cn";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Service Areas", path: "/service-areas/central-isthmus/hillsborough" },
  { name: "About", path: "/about" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Contact", path: "/contact" },
];

const serviceAreaLinks = [
  { name: "Hillsborough (HQ Anchor)", path: "/service-areas/central-isthmus/hillsborough", desc: "0 km dispatch, emergency drainlaying & slope runoff" },
  { name: "Onehunga Volcanic Hub", path: "/service-areas/central-isthmus/onehunga", desc: "Basalt rock soakholes & villa earthenware upgrade" },
  { name: "Mount Roskill Urban Hub", path: "/service-areas/central-isthmus/mt-roskill", desc: "Infill terrace housing drainage & Council PS3 sign-offs" },
  { name: "South Auckland Growth Hub", path: "/service-areas/south-auckland/papakura-drury", desc: "Papakura Veolia connections & Drury subdivision civil" },
  { name: "West Auckland Clay Hub", path: "/service-areas/west-auckland/new-lynn-henderson", desc: "Heavy clay mitigation, French drains & SMAF detention" },
];

const serviceSubLinks = [
  { name: "Drainlaying", path: "/services/drainlaying", desc: "PGDB certified drainlaying & sign-offs" },
  { name: "Public & Private Drainage", path: "/services/drainage", desc: "Complete Auckland drainage solutions" },
  { name: "Private Drainage", path: "/services/private-drainage", desc: "Residential on-site stormwater & sewer" },
  { name: "Public Drainage", path: "/services/public-drainage", desc: "Council main tie-ins & infrastructure" },
  { name: "Drain Unblocking", path: "/services/drain-unblocking", desc: "Hydro-jet clearing & CCTV surveys" },
  { name: "Rainwater Tanks", path: "/services/rainwater-tank-install", desc: "SMAF detention & retention tanks" },
  { name: "Wastewater Systems", path: "/services/wastewater-systems", desc: "Septic & sewer reticulation" },
  { name: "Earthworks & Demolition", path: "/services/earthworks-demolition", desc: "Site cuts, trenching & GD05" },
  { name: "Concrete & Civil", path: "/services/concrete-civil", desc: "Driveways & AT vehicle crossings" },
  { name: "Commercial Plumbing", path: "/services/plumbing", desc: "Water mains & RPZ backflow" },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
    setAreasOpen(false);
  }, [location.pathname]);

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-4 left-4 right-4 z-[100]"
    >
      <div
        className={cn(
          "mx-auto flex max-w-7xl items-center justify-between rounded-[50px] px-6 py-3 transition-all duration-500",
          scrolled
            ? "bg-black/60 backdrop-blur-2xl border border-white/10"
            : "bg-black/40 backdrop-blur-xl border border-white/5"
        )}
      >
        <Link to="/" className="flex items-center gap-3 group">
          <motion.div whileHover={{ scale: 1.05 }}>
            <img
              src="/media/asset-01.png"
              alt="Craft Civil Contractors"
              width={180}
              height={45}
              className="h-10 w-auto sm:h-12"
            />
          </motion.div>
          <div className="hidden sm:block">
            <span className="block text-lg font-bold text-white leading-none">
              Craft Civil
            </span>
            <span className="block text-[10px] uppercase tracking-widest text-white/60">
              Contractors
            </span>
          </div>
        </Link>

        <nav aria-label="Main Navigation" className="hidden lg:flex items-center gap-1 rounded-full bg-white/5 p-1 border border-white/5">
          {navLinks.map((link) => {
            const isServices = link.path === "/services";
            const isAreas = link.name === "Service Areas";
            const isActive = isAreas
              ? location.pathname.startsWith("/service-areas")
              : isServices
              ? location.pathname.startsWith("/services")
              : location.pathname === link.path;

            if (isServices) {
              return (
                <div
                  key={link.path}
                  className="relative group/nav"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <Link
                    to={link.path}
                    className="relative px-3.5 xl:px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1"
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute inset-0 bg-white rounded-full"
                        transition={{
                          type: "spring",
                          bounce: 0.2,
                          duration: 0.6,
                        }}
                      />
                    )}
                    <span
                      className={cn(
                        "relative z-10",
                        isActive
                          ? "text-black"
                          : "text-white/80 hover:text-white"
                      )}
                    >
                      {link.name}
                    </span>
                  </Link>

                  <AnimatePresence>
                    {servicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 rounded-2xl bg-black/90 backdrop-blur-2xl border border-white/10 p-3 shadow-2xl z-[110]"
                      >
                        <div className="grid grid-cols-1 gap-1">
                          {serviceSubLinks.map((sub) => (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              className={cn(
                                "px-3 py-2 rounded-xl transition-all block text-left group/sub",
                                location.pathname === sub.path
                                  ? "bg-white/20 text-white"
                                  : "hover:bg-white/10 text-white/80 hover:text-white"
                              )}
                            >
                              <div className="text-xs font-bold font-heading group-hover/sub:text-white">{sub.name}</div>
                              <div className="text-[10px] text-gray-400 group-hover/sub:text-gray-200">{sub.desc}</div>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            if (isAreas) {
              return (
                <div
                  key={link.path}
                  className="relative group/nav"
                  onMouseEnter={() => setAreasOpen(true)}
                  onMouseLeave={() => setAreasOpen(false)}
                >
                  <Link
                    to={link.path}
                    className="relative px-3.5 xl:px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1"
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute inset-0 bg-white rounded-full"
                        transition={{
                          type: "spring",
                          bounce: 0.2,
                          duration: 0.6,
                        }}
                      />
                    )}
                    <span
                      className={cn(
                        "relative z-10",
                        isActive
                          ? "text-black"
                          : "text-white/80 hover:text-white"
                      )}
                    >
                      {link.name}
                    </span>
                  </Link>

                  <AnimatePresence>
                    {areasOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-84 rounded-2xl bg-black/90 backdrop-blur-2xl border border-white/10 p-3 shadow-2xl z-[110]"
                      >
                        <div className="grid grid-cols-1 gap-1">
                          {serviceAreaLinks.map((sub) => (
                            <Link
                              key={sub.path}
                              to={sub.path}
                              className={cn(
                                "px-3 py-2 rounded-xl transition-all block text-left group/sub",
                                location.pathname === sub.path
                                  ? "bg-white/20 text-white"
                                  : "hover:bg-white/10 text-white/80 hover:text-white"
                              )}
                            >
                              <div className="text-xs font-bold font-heading group-hover/sub:text-white">{sub.name}</div>
                              <div className="text-[10px] text-gray-400 group-hover/sub:text-gray-200">{sub.desc}</div>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            return (
              <Link
                key={link.path}
                to={link.path}
                className="relative px-3.5 xl:px-5 py-2 rounded-full text-sm font-medium transition-colors"
              >
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-white rounded-full"
                    transition={{
                      type: "spring",
                      bounce: 0.2,
                      duration: 0.6,
                    }}
                  />
                )}
                <span
                  className={cn(
                    "relative z-10",
                    isActive
                      ? "text-black"
                      : "text-white/80 hover:text-white"
                  )}
                >
                  {link.name}
                </span>
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href="/contact"
            className="hidden lg:flex items-center gap-2 bg-white text-black px-6 py-2.5 rounded-full font-bold text-sm hover:scale-105 active:scale-95 transition-transform"
          >
            Free Quote
          </a>

          <button
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close menu" : "Open navigation menu"}
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full bg-white/10 border border-white/10 text-white"
          >
            {isOpen ? <HiX size={24} /> : <HiMenu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[90] lg:hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-xl"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="absolute right-4 top-20 w-[calc(100%-2rem)] max-w-sm max-h-[85vh] overflow-y-auto rounded-3xl bg-black/80 backdrop-blur-2xl border border-white/10 p-6"
            >
              <nav className="flex flex-col gap-2">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }}
                  >
                    <Link
                      to={link.path}
                      className={cn(
                        "block px-4 py-2.5 rounded-xl text-base font-medium transition-colors",
                        location.pathname === link.path
                          ? "bg-white text-black"
                          : "text-white hover:bg-white/10"
                      )}
                    >
                      {link.name}
                    </Link>
                    {link.path === "/services" && (
                      <div className="pl-3 mt-1 space-y-1 border-l border-white/15">
                        {serviceSubLinks.map((sub) => (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className={cn(
                              "block px-3 py-1.5 rounded-lg text-xs transition-colors",
                              location.pathname === sub.path
                                ? "bg-white/20 text-white font-bold"
                                : "text-gray-300 hover:text-white"
                            )}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                    {link.name === "Service Areas" && (
                      <div className="pl-3 mt-1 space-y-1 border-l border-white/15">
                        {serviceAreaLinks.map((sub) => (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className={cn(
                              "block px-3 py-1.5 rounded-lg text-xs transition-colors",
                              location.pathname === sub.path
                                ? "bg-white/20 text-white font-bold"
                                : "text-gray-300 hover:text-white"
                            )}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
              </nav>

              <div className="mt-6 space-y-3">
                <a
                  href="tel:+642102984443"
                  className="flex items-center justify-center gap-2 w-full bg-white text-black py-3 rounded-xl font-bold text-sm"
                >
                  <HiPhone className="h-4 w-4" /> Call Now
                </a>
                <a
                  href="/contact"
                  className="flex items-center justify-center w-full bg-white/10 text-white border border-white/20 py-3 rounded-xl font-bold text-sm hover:bg-white/20 transition-colors"
                >
                  Get Free Quote
                </a>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
